import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';
import type { ID, NotificationSettings, Org, Project, Role } from '@proofroom/schema';
import { useSession } from '../auth/session';
import { Header } from '../components/Header';
import { Switch } from '../components/Switch';
import { Chip, EmptyState, ErrorState, Spinner, formatBytes, formatDate, timeAgo } from '../components/ui';
import {
  users, projects as projectsApi, orgs as orgsApi, backups, storage,
  type AdminNotificationPatch, type BackupRun, type DocumentStorage, type StorageReport, type UserWithProjects,
} from '../api/endpoints';
import { ApiError } from '../api/client';

const ROLES: Role[] = ['admin', 'designer', 'client'];
const MIN_PASSWORD_LENGTH = 12;

function projectNames(ids: ID[], list: Project[]): string {
  const byId = new Map(list.map((p) => [p.id, p.name]));
  const names = ids.map((id) => byId.get(id)).filter((n): n is string => !!n);
  return names.length ? names.join(', ') : 'None';
}

/** A client-kind org can only hold role client; an agency-kind org holds any role (services/users.ts assertOrgHoldsRole mirrors this). */
function orgDisallowsRole(org: Org, role: Role): boolean {
  return org.kind === 'client' && role !== 'client';
}

/** Shared between the create form and row edit: the disallowed combination is disabled, with why. */
function OrgOptions({ orgList, role }: { orgList: Org[]; role: Role }) {
  return (
    <>
      {orgList.map((o) => {
        const disabled = orgDisallowsRole(o, role);
        return (
          <option key={o.id} value={o.id} disabled={disabled} title={disabled ? 'A client organisation can only hold client users' : undefined}>
            {o.name}{o.kind === 'client' ? ' (client)' : ''}
          </option>
        );
      })}
    </>
  );
}

/** The ticked projects' clientOrgId, when they agree on exactly one non-null value; undefined if none is ticked yet, or they disagree. */
function tickedProjectsOrgId(projectIds: ID[], projectList: Project[]): ID | undefined {
  const byId = new Map(projectList.map((p) => [p.id, p]));
  const ids = new Set<ID>();
  for (const id of projectIds) {
    const clientOrgId = byId.get(id)?.clientOrgId;
    if (clientOrgId) ids.add(clientOrgId);
  }
  return ids.size === 1 ? [...ids][0] : undefined;
}

/**
 * The admin's own org for designer/admin. For client: the ticked projects' clientOrgId when
 * they agree on exactly one, else the first client-kind org (today's fallback when nothing is
 * ticked or the ticks disagree). '' if none fits yet (org list still loading).
 */
function defaultOrgId(role: Role, orgList: Org[], adminOrgId: ID | undefined, projectIds: ID[], projectList: Project[]): ID {
  if (role === 'client') {
    return tickedProjectsOrgId(projectIds, projectList) ?? orgList.find((o) => o.kind === 'client')?.id ?? '';
  }
  return adminOrgId ?? '';
}

/**
 * One user's email switches as the admin sees them, through the admin routes: the master
 * switch and one per project they are on, each saved as it is flipped. Loaded when the
 * disclosure opens, not with the table, so the list is one request however many users.
 */
function AlertsPanel({ u }: { u: UserWithProjects }) {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<ID | 'email' | null>(null);
  const [saved, setSaved] = useState<ID | 'email' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    users.notifications(u.id)
      .then((s) => { if (alive) setSettings(s); })
      .catch((err) => { if (alive) setLoadError(err instanceof ApiError ? err.message : 'Could not load these alerts'); });
    return () => { alive = false; };
  }, [u.id]);

  const apply = async (key: ID | 'email', patch: AdminNotificationPatch) => {
    setBusy(key);
    setSaved(null);
    setError(null);
    try {
      setSettings(await users.saveNotifications(u.id, patch));
      setSaved(key);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the switch');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div role="region" aria-label={`Alerts for ${u.name}`} style={{ padding: '4px 8px 8px', maxWidth: 520 }}>
      {loadError && <p className="error-text small" role="alert">{loadError}</p>}
      {!loadError && !settings && <Spinner label="Loading alerts" />}
      {settings && (
        <>
          <div className="switch-row">
            <div>
              <span>Email alerts</span>
              {saved === 'email' && <span className="muted small" style={{ marginLeft: 8 }}>Saved</span>}
            </div>
            <Switch label="Email alerts" checked={settings.emailEnabled} busy={busy === 'email'} onChange={(on) => void apply('email', { emailEnabled: on })} />
          </div>
          {settings.projects.length === 0 && <p className="muted small">Not on any projects.</p>}
          {settings.projects.map((p) => (
            <div key={p.projectId} className="switch-row">
              <div>
                <span>{p.name}</span>
                {saved === p.projectId && <span className="muted small" style={{ marginLeft: 8 }}>Saved</span>}
              </div>
              <Switch
                label={`Email alerts for ${p.name}`}
                checked={!p.muted}
                busy={busy === p.projectId}
                onChange={(on) => void apply(p.projectId, { mutes: [{ projectId: p.projectId, muted: !on }] })}
              />
            </div>
          ))}
          {error && <p className="error-text small" role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}

interface UserRowProps {
  u: UserWithProjects;
  projectList: Project[];
  orgList: Org[];
  editing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (patch: { name: string; role: Role; projectIds: ID[]; orgId: ID }) => void;
  onSetPassword: () => void;
  onSendReset: () => void;
  onDelete: () => void;
  note?: string;
  rowError?: string;
}

function UserRow({ u, projectList, orgList, editing, onStartEdit, onCancelEdit, onSave, onSetPassword, onSendReset, onDelete, note, rowError }: UserRowProps) {
  const [name, setName] = useState(u.name);
  const [role, setRole] = useState<Role>(u.role);
  const [projectIds, setProjectIds] = useState<ID[]>(u.projectIds);
  const [orgId, setOrgId] = useState<ID>(u.orgId);
  const [alertsOpen, setAlertsOpen] = useState(false);
  // Latest row data for the effect below to read without making it re-run (and so
  // discard an open edit) on every refresh, which hands every row a new `u` reference.
  const uRef = useRef(u);
  uRef.current = u;

  useEffect(() => {
    if (editing) { setName(uRef.current.name); setRole(uRef.current.role); setProjectIds(uRef.current.projectIds); setOrgId(uRef.current.orgId); }
  }, [editing]);

  const toggleProject = (id: ID) => {
    setProjectIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  if (editing) {
    return (
      <tr>
        <td><input className="input" aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} /></td>
        <td>{u.email}</td>
        <td>
          <select className="input" aria-label="Organisation" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <OrgOptions orgList={orgList} role={role} />
          </select>
        </td>
        <td>
          <select className="input" aria-label="Role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </td>
        <td>
          <div className="btn-row">
            {projectList.map((p) => (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={projectIds.includes(p.id)} onChange={() => toggleProject(p.id)} />
                {p.name}
              </label>
            ))}
          </div>
        </td>
        <td className="mono muted">{formatDate(u.createdAt)}</td>
        <td>
          <div className="btn-row">
            <button className="btn sm primary" onClick={() => onSave({ name, role, projectIds, orgId })}>Save</button>
            <button className="btn sm ghost" onClick={onCancelEdit}>Cancel</button>
          </div>
          {rowError && <div className="error-text small">{rowError}</div>}
        </td>
      </tr>
    );
  }

  return (
    <>
      <tr>
        <td>{u.name}</td>
        <td>{u.email}</td>
        <td>{u.orgName}</td>
        <td><Chip kind="role">{u.role}</Chip></td>
        <td>{projectNames(u.projectIds, projectList)}</td>
        <td className="mono muted">{formatDate(u.createdAt)}</td>
        <td>
          <div className="btn-row">
            <button className="btn sm ghost" onClick={onStartEdit}>Edit</button>
            <button className="btn sm ghost" onClick={onSetPassword}>Set password</button>
            <button className="btn sm ghost" onClick={onSendReset}>Send reset link</button>
            <button className={alertsOpen ? 'btn sm on' : 'btn sm ghost'} aria-expanded={alertsOpen} onClick={() => setAlertsOpen((v) => !v)}>Alerts</button>
            <button className="btn sm danger" onClick={onDelete}>Delete</button>
          </div>
          {note && <div className="small muted">{note}</div>}
          {rowError && <div className="error-text small">{rowError}</div>}
        </td>
      </tr>
      {alertsOpen && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--bg)' }}><AlertsPanel u={u} /></td>
        </tr>
      )}
    </>
  );
}

function OperationsCard({ runs, error }: { runs: BackupRun[] | null; error: string | null }) {
  const latest = runs && runs.length > 0 ? runs[0] : null;
  return (
    <div className="card" aria-label="Operations" style={{ padding: 16, marginBottom: 20 }}>
      <h2 style={{ marginTop: 0 }}>Operations</h2>
      {error && <p className="error-text small">{error}</p>}
      {!error && runs === null && <Spinner label="Loading backups" />}
      {!error && runs !== null && !latest && <p className="small muted">No backups yet.</p>}
      {latest && (
        <>
          <div className="btn-row" style={{ gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div className="label">Last backup</div>
              <div>{formatDate(latest.finishedAt)}</div>
              <div className="small muted">{timeAgo(latest.finishedAt)}</div>
            </div>
            <div>
              <div className="label">Status</div>
              <Chip kind={latest.ok ? 'ok' : 'danger'}>{latest.ok ? 'OK' : 'Failed'}</Chip>
            </div>
            <div>
              <div className="label">Database</div>
              <div>{formatBytes(latest.dbBytes)}</div>
            </div>
            <div>
              <div className="label">Data</div>
              <div>{formatBytes(latest.dataBytes)}</div>
            </div>
          </div>
          {!latest.ok && latest.error && <p className="error-text small" style={{ marginTop: 8 }}>{latest.error}</p>}
        </>
      )}
    </div>
  );
}

/**
 * What each document is holding on disk, and what retention would delete. The Prune button
 * only exists when the server reports a keep above 0: with retention off there is nothing to
 * press, rather than a button that answers 409.
 */
function StorageCard({ report, error, pruneError, note, busyId, onPrune }: {
  report: StorageReport | null;
  error: string | null;
  /** The last prune's own failure. Kept apart from `error` so refetching the report cannot wipe it. */
  pruneError: string | null;
  note: string | null;
  busyId: ID | null;
  onPrune: (d: DocumentStorage) => void;
}) {
  return (
    <div className="card" aria-label="Storage" style={{ padding: 16, marginBottom: 20 }}>
      <h2 style={{ marginTop: 0 }}>Storage</h2>
      {error && <p className="error-text small">{error}</p>}
      {!error && report === null && <Spinner label="Loading storage" />}
      {report && report.documents.length === 0 && <p className="small muted">No documents yet.</p>}
      {report && report.documents.length > 0 && (
        <table aria-label="Documents on disk">
          <thead><tr><th>Document</th><th>Drafts</th><th>On disk</th><th></th></tr></thead>
          <tbody>
            {report.documents.map((d) => (
              <tr key={d.documentId}>
                <td>{d.name}</td>
                <td className="small muted">
                  {`${d.drafts} draft${d.drafts === 1 ? '' : 's'}`}
                  {d.wouldPrune.length > 0 && `, ${d.wouldPrune.length} would prune`}
                  {d.pruned > 0 && ` (${d.pruned} pruned)`}
                </td>
                <td className="mono">{formatBytes(d.bytes)}</td>
                <td>
                  {report.keep > 0 && d.wouldPrune.length > 0 && (
                    <button className="btn sm danger" disabled={busyId === d.documentId} onClick={() => onPrune(d)}>Prune</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {pruneError && <p className="error-text small" role="alert">{pruneError}</p>}
      {note && <p className="small muted" style={{ marginBottom: 0 }}>{note}</p>}
      {report && (
        <p className="small muted" style={{ marginBottom: 0 }}>
          {report.keep > 0
            ? `Keeping the newest ${report.keep} drafts of each document, its current draft, every approved draft and anything with a change still waiting. Pruning deletes the rest of the files; the drafts themselves stay.`
            : 'Retention is off, so nothing is ever pruned. Set RETENTION_KEEP_DRAFTS on the server to start reclaiming old drafts\' files.'}
        </p>
      )}
    </div>
  );
}

export function Users() {
  const { user, loading } = useSession();
  const [rows, setRows] = useState<UserWithProjects[] | null>(null);
  const [projectList, setProjectList] = useState<Project[]>([]);
  const [orgList, setOrgList] = useState<Org[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [backupRuns, setBackupRuns] = useState<BackupRun[] | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);
  const [storageReport, setStorageReport] = useState<StorageReport | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [storageNote, setStorageNote] = useState<string | null>(null);
  const [pruneError, setPruneError] = useState<string | null>(null);
  const [pruningId, setPruningId] = useState<ID | null>(null);

  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<Role>('client');
  const [newProjectIds, setNewProjectIds] = useState<ID[]>([]);
  const [newOrgId, setNewOrgId] = useState<ID>('');
  // Set only by a manual pick (the select, or creating a new org); once true, ticking projects
  // or changing role no longer overrides the admin's own choice.
  const [orgTouched, setOrgTouched] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);

  const [showNewOrg, setShowNewOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [orgError, setOrgError] = useState<string | null>(null);
  const [orgBusy, setOrgBusy] = useState(false);

  const [editingId, setEditingId] = useState<ID | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const isAdmin = !!user && user.role === 'admin';

  const refresh = useCallback(async () => {
    try {
      const [u, p, o] = await Promise.all([users.list(), projectsApi.list(), orgsApi.list()]);
      setRows(u);
      setProjectList(p);
      setOrgList(o);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load users');
    }
  }, []);

  // Independent of refresh(): a backups failure should not blank the user table, and vice versa.
  const refreshBackups = useCallback(async () => {
    try {
      setBackupRuns(await backups.list());
      setBackupError(null);
    } catch (err) {
      setBackupError(err instanceof ApiError ? err.message : 'Could not load backups');
    }
  }, []);

  const refreshStorage = useCallback(async () => {
    try {
      setStorageReport(await storage.report());
      setStorageError(null);
    } catch (err) {
      setStorageError(err instanceof ApiError ? err.message : 'Could not load storage');
    }
  }, []);

  useEffect(() => {
    if (isAdmin) { void refresh(); void refreshBackups(); void refreshStorage(); }
  }, [isAdmin, refresh, refreshBackups, refreshStorage]);

  // Untouched, the effective value is always the current default (recomputed from the ticked
  // projects and the role on every render, per the plan); once the admin picks one by hand,
  // that choice sticks regardless of further ticks or a role change. Computed at render rather
  // than chased with an effect so there is no frame where the select shows a stale or empty
  // value while orgs are still in flight.
  const effectiveNewOrgId = orgTouched ? newOrgId : defaultOrgId(newRole, orgList, user?.orgId, newProjectIds, projectList);

  const setNote = (id: ID, msg: string) => {
    setNotes((prev) => ({ ...prev, [id]: msg }));
    setRowErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });
  };
  const setRowErr = (id: ID, msg: string) => {
    setRowErrors((prev) => ({ ...prev, [id]: msg }));
    setNotes((prev) => { const next = { ...prev }; delete next[id]; return next; });
  };
  const clearRowMessages = (id: ID) => {
    setRowErrors((prev) => { const next = { ...prev }; delete next[id]; return next; });
    setNotes((prev) => { const next = { ...prev }; delete next[id]; return next; });
  };

  const toggleNewProject = (id: ID) => {
    setNewProjectIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const submitCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (newPassword.length < MIN_PASSWORD_LENGTH) { setCreateError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`); return; }
    setCreateBusy(true);
    try {
      await users.create({ name: newName, email: newEmail, role: newRole, password: newPassword, projectIds: newProjectIds, orgId: effectiveNewOrgId || undefined });
      setNewName(''); setNewEmail(''); setNewRole('client'); setNewProjectIds([]); setNewPassword(''); setNewOrgId(''); setOrgTouched(false);
      await refresh();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Could not create the user');
    } finally { setCreateBusy(false); }
  };

  /** Only offered while the new user's role is client: what POST /api/orgs creates is always client-kind, so it fits no other role. */
  const handleCreateOrg = async () => {
    setOrgError(null);
    setOrgBusy(true);
    try {
      const org = await orgsApi.create(newOrgName.trim());
      setOrgList((prev) => [...prev, org]);
      setNewOrgId(org.id);
      setOrgTouched(true);
      setNewOrgName('');
      setShowNewOrg(false);
    } catch (err) {
      setOrgError(err instanceof ApiError ? err.message : 'Could not create the organisation');
    } finally { setOrgBusy(false); }
  };

  const handleSave = async (u: UserWithProjects, patch: { name: string; role: Role; projectIds: ID[]; orgId: ID }) => {
    try {
      await users.update(u.id, patch);
      setEditingId(null);
      clearRowMessages(u.id);
      await refresh();
    } catch (err) {
      setRowErr(u.id, err instanceof ApiError ? err.message : 'Could not save changes');
    }
  };

  const handleSetPassword = async (u: UserWithProjects) => {
    const pw = window.prompt(`New password for ${u.email}`);
    if (pw === null) return;
    if (pw.length < MIN_PASSWORD_LENGTH) { setRowErr(u.id, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`); return; }
    try {
      await users.setPassword(u.id, pw);
      setNote(u.id, 'Password set');
      await refresh();
    } catch (err) {
      setRowErr(u.id, err instanceof ApiError ? err.message : 'Could not set the password');
    }
  };

  const handleSendReset = async (u: UserWithProjects) => {
    if (!window.confirm(`Send a password reset link to ${u.email}?`)) return;
    try {
      await users.sendReset(u.id);
      setNote(u.id, `Reset link sent to ${u.email}`);
      await refresh();
    } catch (err) {
      setRowErr(u.id, err instanceof ApiError ? err.message : 'Could not send the reset link');
    }
  };

  const handleDelete = async (u: UserWithProjects) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await users.remove(u.id);
      await refresh();
    } catch (err) {
      setRowErr(u.id, err instanceof ApiError ? err.message : 'Could not delete this user');
    }
  };

  /**
   * Deleting files is not undoable, so the confirm names the exact draft numbers going, and
   * those same numbers travel with the request: the server recomputes the plan and refuses
   * with a 409 if it has moved since this report was fetched, which is the one case where
   * refetching it is the answer rather than retrying.
   */
  const handlePrune = async (d: DocumentStorage) => {
    const numbers = d.wouldPrune.join(', ');
    if (!window.confirm(`Prune ${d.wouldPrune.length} draft(s) of ${d.name}: ${numbers}. Their files cannot be recovered except from a backup. Continue?`)) return;
    setPruningId(d.documentId);
    setStorageNote(null);
    setPruneError(null);
    try {
      const result = await storage.prune(d.documentId, d.wouldPrune);
      const done = result.pruned.length ? `Pruned drafts ${result.pruned.join(', ')} of ${d.name}` : `Nothing left to prune in ${d.name}`;
      setStorageNote(result.failed.length ? `${done}. ${result.failed.map((f) => f.number).join(', ')} could not be pruned: ${result.failed[0].error}` : done);
    } catch (err) {
      setPruneError(err instanceof ApiError ? err.message : 'Could not prune this document');
    } finally {
      setPruningId(null);
      await refreshStorage();
    }
  };

  if (loading) return <div className="app"><Spinner label="Checking your session" /></div>;
  if (!isAdmin) return <Navigate to="/projects" replace />;

  return (
    <div className="app">
      <Header crumbs={[{ label: 'Projects', to: '/projects' }, { label: 'Users' }]} />
      <main className="main">
        <div className="page users" style={{ maxWidth: 1100 }}>
          <div className="page-head"><h1>Users</h1><span className="sub">{rows ? `${rows.length} user${rows.length === 1 ? '' : 's'}` : ''}</span></div>

          <form className="card" aria-label="Add user" onSubmit={(e) => void submitCreate(e)} style={{ padding: 16, marginBottom: 20 }}>
            <h2 style={{ marginTop: 0 }}>Add a user</h2>
            <div className="field">
              <label className="label" htmlFor="new-name">Name</label>
              <input id="new-name" className="input" value={newName} onChange={(e) => setNewName(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label" htmlFor="new-email">Email</label>
              <input id="new-email" className="input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
            </div>
            <div className="field">
              <label className="label" htmlFor="new-role">Role</label>
              <select id="new-role" className="input" value={newRole} onChange={(e) => setNewRole(e.target.value as Role)}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="new-org">Organisation</label>
              <select id="new-org" className="input" value={effectiveNewOrgId} onChange={(e) => { setNewOrgId(e.target.value); setOrgTouched(true); }}>
                <OrgOptions orgList={orgList} role={newRole} />
              </select>
              {newRole === 'client' && !showNewOrg && (
                <button type="button" className="btn sm ghost" style={{ marginTop: 8 }} onClick={() => setShowNewOrg(true)}>New organisation</button>
              )}
              {newRole === 'client' && showNewOrg && (
                <div className="btn-row" style={{ marginTop: 8 }}>
                  <input
                    className="input" aria-label="New organisation name" placeholder="Organisation name"
                    value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)}
                  />
                  <button type="button" className="btn sm" disabled={orgBusy || !newOrgName.trim()} onClick={() => void handleCreateOrg()}>
                    {orgBusy ? 'Creating' : 'Create'}
                  </button>
                  <button type="button" className="btn sm ghost" onClick={() => { setShowNewOrg(false); setNewOrgName(''); setOrgError(null); }}>Cancel</button>
                </div>
              )}
              {orgError && <p className="error-text small">{orgError}</p>}
            </div>
            <div className="field">
              <span className="label">Projects</span>
              <div className="btn-row">
                {projectList.map((p) => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="checkbox" checked={newProjectIds.includes(p.id)} onChange={() => toggleNewProject(p.id)} />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="field">
              <label className="label" htmlFor="new-password">Initial password</label>
              <input id="new-password" className="input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
            </div>
            {createError && <p className="error-text" role="alert" style={{ marginBottom: 12 }}>{createError}</p>}
            <button className="btn primary" type="submit" disabled={createBusy}>{createBusy ? 'Adding' : 'Add user'}</button>
          </form>

          <OperationsCard runs={backupRuns} error={backupError} />

          <StorageCard report={storageReport} error={storageError} pruneError={pruneError} note={storageNote} busyId={pruningId} onPrune={(d) => void handlePrune(d)} />

          {loadError && <ErrorState title="Could not load users" error={loadError} retry={() => void refresh()} />}
          {!loadError && rows === null && <Spinner label="Loading users" />}
          {!loadError && rows && rows.length === 0 && <EmptyState title="No users yet">Add the first user above.</EmptyState>}
          {!loadError && rows && rows.length > 0 && (
            <div className="card" style={{ overflow: 'auto' }}>
              <table aria-label="Users">
                <thead><tr><th>Name</th><th>Email</th><th>Organisation</th><th>Role</th><th>Projects</th><th>Created</th><th></th></tr></thead>
                <tbody>
                  {rows.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      projectList={projectList}
                      orgList={orgList}
                      editing={editingId === u.id}
                      onStartEdit={() => setEditingId(u.id)}
                      onCancelEdit={() => setEditingId(null)}
                      onSave={(patch) => void handleSave(u, patch)}
                      onSetPassword={() => void handleSetPassword(u)}
                      onSendReset={() => void handleSendReset(u)}
                      onDelete={() => void handleDelete(u)}
                      note={notes[u.id]}
                      rowError={rowErrors[u.id]}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
